import { useState } from 'react';
import { submitContactForm } from '@/lib/api/forms';
import { ScrollReveal } from '@/components/ui/scroll-reveal';
import { Honeypot } from '@/components/forms/Honeypot';
import { useHoneypot } from '@/components/forms/use-honeypot';

const BEDROOM_PRICES: Record<string, number> = { '1': 150, '2': 200, '3': 280, '4': 350, '5+': 420 };
const CLEAN_MULTIPLIERS: Record<string, number> = { standard: 1, deep: 1.7, moveout: 2.0 };
const CONDITION_MULTIPLIERS: Record<string, number> = { maintained: 1, attention: 1.2, heavy: 1.4 };
const ADDONS = [
  { id: 'oven', label: 'Inside Oven', price: 35 },
  { id: 'fridge', label: 'Inside Fridge', price: 30 },
  { id: 'windows', label: 'Inside Windows', price: 40 },
  { id: 'laundry', label: 'Laundry (per load)', price: 25 },
];

const COMMERCIAL_RATES: Record<string, number> = {
  office: 0.13,
  medical: 0.22,
  property: 0.09,
  retail: 0.11,
  warehouse: 0.10,
};
const FACILITY_LABELS: Record<string, string> = {
  office: 'General Office',
  medical: 'Medical-Dental',
  property: 'Property Mgmt',
  retail: 'Retail',
  warehouse: 'Warehouse',
};
const FREQ_MULTIPLIERS: Record<string, number> = {
  daily: 22,
  '3x': 13,
  '2x': 9,
  weekly: 4.3,
  biweekly: 2.15,
};
const FREQ_LABELS: Record<string, string> = {
  daily: 'Daily',
  '3x': '3x / Week',
  '2x': '2x / Week',
  weekly: 'Weekly',
  biweekly: 'Bi-Weekly',
};
const COMMERCIAL_ADDONS = [
  { id: 'hepa', label: 'HEPA Vacuuming' },
  { id: 'electrostatic', label: 'Electrostatic Disinfection' },
  { id: 'floor', label: 'Floor Care' },
  { id: 'windows', label: 'Window Cleaning' },
];

function fmt(n: number) {
  return '$' + Math.round(n).toLocaleString();
}

export function PricingCalculator() {
  const [tab, setTab] = useState<'residential' | 'commercial'>('residential');
  const { value: hp, honeypotProps } = useHoneypot();

  const [bedrooms, setBedrooms] = useState('2');
  const [bathrooms, setBathrooms] = useState('1');
  const [cleanType, setCleanType] = useState('standard');
  const [addons, setAddons] = useState<string[]>([]);
  const [condition, setCondition] = useState('maintained');

  const [sqft, setSqft] = useState('');
  const [facility, setFacility] = useState('office');
  const [frequency, setFrequency] = useState('weekly');
  const [commAddons, setCommAddons] = useState<string[]>([]);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const resBase = BEDROOM_PRICES[bedrooms] || 200;
  const resBathAddon = Math.max(0, parseInt(bathrooms) - 1) * 35;
  const resAddonTotal = addons.reduce((sum, id) => sum + (ADDONS.find((a) => a.id === id)?.price || 0), 0);
  const resSubtotal = (resBase + resBathAddon) * CLEAN_MULTIPLIERS[cleanType] * CONDITION_MULTIPLIERS[condition] + resAddonTotal;
  const resLow = Math.round(resSubtotal * 0.9);
  const resHigh = Math.round(resSubtotal * 1.15);

  const sqftNum = parseInt(sqft) || 0;
  const commBaseRate = COMMERCIAL_RATES[facility] || 0.13;
  const commAddonMult = 1 + commAddons.length * 0.15;
  const commPerClean = sqftNum * commBaseRate * commAddonMult;
  const commMonthly = commPerClean * FREQ_MULTIPLIERS[frequency];

  const toggle = (id: string, list: string[], setter: (v: string[]) => void) => {
    setter(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  };

  const buildMessage = () => {
    if (tab === 'residential') {
      const lines = [
        `[CALCULATOR QUOTE — Residential]`,
        `Bedrooms: ${bedrooms}`,
        `Bathrooms: ${bathrooms}`,
        `Cleaning Type: ${cleanType}`,
        `Condition: ${condition}`,
        `Add-ons: ${addons.length ? addons.map((id) => ADDONS.find((a) => a.id === id)?.label).join(', ') : 'None'}`,
        `Estimate Range: ${fmt(resLow)} – ${fmt(resHigh)}`,
      ];
      if (form.notes) lines.push(`Notes: ${form.notes}`);
      return lines.join('\n');
    }
    const lines = [
      `[CALCULATOR QUOTE — Commercial]`,
      `Square Footage: ${sqft}`,
      `Facility Type: ${FACILITY_LABELS[facility]}`,
      `Frequency: ${FREQ_LABELS[frequency]}`,
      `Special Requirements: ${commAddons.length ? commAddons.map((id) => COMMERCIAL_ADDONS.find((a) => a.id === id)?.label).join(', ') : 'None'}`,
      `Per-Clean Estimate: ${fmt(commPerClean)}`,
      `Monthly Estimate: ${fmt(commMonthly)}`,
    ];
    if (form.notes) lines.push(`Notes: ${form.notes}`);
    return lines.join('\n');
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await submitContactForm(
        {
          name: form.name,
          email: form.email,
          phone: form.phone,
          message: buildMessage(),
        },
        'pricing_calculator',
        false,
        hp,
      );
      setStatus('sent');
      setForm({ name: '', email: '', phone: '', notes: '' });
    } catch {
      setStatus('error');
    }
  };

  const inputClass = 'bold-input';
  const radio = (active: boolean) =>
    `font-display tracking-wider text-xs px-4 py-3 border-2 cursor-pointer transition ${
      active ? 'border-electric bg-electric/20 text-electric' : 'border-white/20 text-white/60 hover:border-electric/50'
    }`;
  const check = (active: boolean) =>
    `flex items-center gap-3 px-4 py-3 border cursor-pointer transition text-sm ${
      active ? 'border-thunder bg-thunder/10 text-thunder' : 'border-white/20 text-white/60 hover:border-thunder/50'
    }`;

  return (
    <section className="relative py-28 bg-charcoal overflow-hidden">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <ScrollReveal className="text-center mb-14">
          <div className="text-thunder font-display tracking-[0.4em] text-xs mb-3">// ESTIMATE BUILDER</div>
          <h2 className="font-display font-bold text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.9]">
            BUILD YOUR <span className="text-electric">QUOTE</span>
          </h2>
          <p className="text-white/60 text-lg mt-6 max-w-xl mx-auto">
            Get an instant estimate. Final pricing confirmed after walkthrough.
          </p>
        </ScrollReveal>

        <div className="flex justify-center gap-4 mb-12">
          {(['residential', 'commercial'] as const).map((t) => (
            <button
              key={t}
              onClick={() => { setTab(t); setShowForm(false); setStatus('idle'); }}
              className={`font-display font-bold px-8 py-4 tracking-[0.3em] text-sm transition ${
                tab === t ? 'bg-electric text-charcoal-deep glow-electric' : 'border-2 border-electric/40 text-electric/70 hover:border-electric'
              }`}
            >
              {t.toUpperCase()}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-[1fr_340px] gap-10">
          <ScrollReveal direction="left">
            <div className="bg-charcoal-deep border border-electric/20 p-8 lg:p-10">
              {tab === 'residential' ? (
                <div className="space-y-8">
                  <div>
                    <label className="font-display text-electric tracking-[0.3em] text-xs block mb-3">BEDROOMS</label>
                    <div className="flex flex-wrap gap-2">
                      {Object.keys(BEDROOM_PRICES).map((v) => (
                        <button key={v} onClick={() => setBedrooms(v)} className={radio(bedrooms === v)}>{v}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="font-display text-electric tracking-[0.3em] text-xs block mb-3">BATHROOMS</label>
                    <div className="flex flex-wrap gap-2">
                      {['1', '2', '3', '4+'].map((v) => (
                        <button key={v} onClick={() => setBathrooms(v.replace('+', ''))} className={radio(bathrooms === v.replace('+', ''))}>{v}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="font-display text-electric tracking-[0.3em] text-xs block mb-3">CLEANING TYPE</label>
                    <div className="flex flex-wrap gap-2">
                      {[{ id: 'standard', l: 'STANDARD' }, { id: 'deep', l: 'DEEP CLEAN' }, { id: 'moveout', l: 'MOVE-OUT' }].map((t) => (
                        <button key={t.id} onClick={() => setCleanType(t.id)} className={radio(cleanType === t.id)}>{t.l}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="font-display text-electric tracking-[0.3em] text-xs block mb-3">ADD-ONS</label>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {ADDONS.map((a) => (
                        <button key={a.id} onClick={() => toggle(a.id, addons, setAddons)} className={check(addons.includes(a.id))}>
                          <span className={`w-4 h-4 border flex items-center justify-center text-xs ${addons.includes(a.id) ? 'border-thunder bg-thunder text-charcoal-deep' : 'border-white/30'}`}>
                            {addons.includes(a.id) && '✓'}
                          </span>
                          <span className="font-display tracking-wider">{a.label}</span>
                          <span className="ml-auto text-white/40">${a.price}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="font-display text-electric tracking-[0.3em] text-xs block mb-3">HOME CONDITION</label>
                    <div className="flex flex-wrap gap-2">
                      {[{ id: 'maintained', l: 'WELL MAINTAINED' }, { id: 'attention', l: 'NEEDS ATTENTION' }, { id: 'heavy', l: 'HEAVY CLEAN' }].map((c) => (
                        <button key={c.id} onClick={() => setCondition(c.id)} className={radio(condition === c.id)}>{c.l}</button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-8">
                  <div>
                    <label className="font-display text-electric tracking-[0.3em] text-xs block mb-3">SQUARE FOOTAGE</label>
                    <input type="number" className={inputClass} placeholder="e.g. 5000" value={sqft} onChange={(e) => setSqft(e.target.value)} min="0" />
                  </div>
                  <div>
                    <label className="font-display text-electric tracking-[0.3em] text-xs block mb-3">FACILITY TYPE</label>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(FACILITY_LABELS).map(([id, label]) => (
                        <button key={id} onClick={() => setFacility(id)} className={radio(facility === id)}>{label.toUpperCase()}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="font-display text-electric tracking-[0.3em] text-xs block mb-3">FREQUENCY</label>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(FREQ_LABELS).map(([id, label]) => (
                        <button key={id} onClick={() => setFrequency(id)} className={radio(frequency === id)}>{label.toUpperCase()}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="font-display text-electric tracking-[0.3em] text-xs block mb-3">SPECIAL REQUIREMENTS</label>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {COMMERCIAL_ADDONS.map((a) => (
                        <button key={a.id} onClick={() => toggle(a.id, commAddons, setCommAddons)} className={check(commAddons.includes(a.id))}>
                          <span className={`w-4 h-4 border flex items-center justify-center text-xs ${commAddons.includes(a.id) ? 'border-thunder bg-thunder text-charcoal-deep' : 'border-white/30'}`}>
                            {commAddons.includes(a.id) && '✓'}
                          </span>
                          <span className="font-display tracking-wider">{a.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ScrollReveal>

          <ScrollReveal direction="right">
            <div className="bg-charcoal-deep border-2 border-thunder p-6 sm:p-8 sticky top-20 lg:top-28 glow-thunder">
              <div className="font-display text-thunder tracking-[0.3em] text-xs mb-6">// YOUR ESTIMATE</div>

              {tab === 'residential' ? (
                <>
                  <div className="font-display font-bold text-white text-5xl leading-none mb-2" style={{ textShadow: '0 0 30px rgba(255,214,10,.3)' }}>
                    {fmt(resLow)} – {fmt(resHigh)}
                  </div>
                  <div className="text-white/50 text-sm mb-8">one-time estimate</div>
                  <div className="space-y-2 text-sm text-white/60 border-t border-white/10 pt-6 mb-8">
                    <div className="flex justify-between"><span>Base ({bedrooms} BR)</span><span>{fmt(BEDROOM_PRICES[bedrooms])}</span></div>
                    {parseInt(bathrooms) > 1 && <div className="flex justify-between"><span>Extra bathrooms</span><span>+{fmt(resBathAddon)}</span></div>}
                    {cleanType !== 'standard' && <div className="flex justify-between"><span>{cleanType === 'deep' ? 'Deep clean' : 'Move-out'}</span><span>{CLEAN_MULTIPLIERS[cleanType]}x</span></div>}
                    {condition !== 'maintained' && <div className="flex justify-between"><span>Condition</span><span>+{Math.round((CONDITION_MULTIPLIERS[condition] - 1) * 100)}%</span></div>}
                    {addons.map((id) => { const a = ADDONS.find((x) => x.id === id); return a ? <div key={id} className="flex justify-between"><span>{a.label}</span><span>+${a.price}</span></div> : null; })}
                  </div>
                </>
              ) : sqftNum > 0 ? (
                <>
                  <div className="font-display font-bold text-white text-4xl leading-none mb-1" style={{ textShadow: '0 0 30px rgba(255,214,10,.3)' }}>
                    {fmt(commPerClean)}
                  </div>
                  <div className="text-white/50 text-sm mb-2">per clean</div>
                  <div className="font-display font-bold text-electric text-3xl leading-none mb-1">{fmt(commMonthly)}</div>
                  <div className="text-white/50 text-sm mb-8">per month ({FREQ_LABELS[frequency]})</div>
                  <div className="space-y-2 text-sm text-white/60 border-t border-white/10 pt-6 mb-8">
                    <div className="flex justify-between"><span>{sqft} sqft × ${commBaseRate}</span><span>{fmt(sqftNum * commBaseRate)}</span></div>
                    {commAddons.length > 0 && <div className="flex justify-between"><span>Requirements</span><span>+{commAddons.length * 15}%</span></div>}
                    <div className="flex justify-between"><span>{FREQ_LABELS[frequency]}</span><span>×{FREQ_MULTIPLIERS[frequency]}/mo</span></div>
                  </div>
                </>
              ) : (
                <div className="text-white/40 text-center py-8 font-display tracking-wider text-sm">
                  ENTER SQ FT TO SEE ESTIMATE
                </div>
              )}

              {!showForm ? (
                <button onClick={() => setShowForm(true)} className="w-full bg-thunder hover:bg-thunder-dark text-charcoal-deep font-display font-bold py-4 tracking-[0.25em] text-sm transition">
                  GET YOUR EXACT QUOTE →
                </button>
              ) : status === 'sent' ? (
                <div className="text-center py-4">
                  <div className="font-display text-thunder tracking-wider text-sm mb-2">✓ QUOTE REQUESTED</div>
                  <p className="text-white/60 text-sm">We'll respond within one business hour.</p>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-4">
                  <Honeypot {...honeypotProps} id="pricing_company_website" />
                  <input className={inputClass} placeholder="FULL NAME" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  <input className={inputClass} placeholder="EMAIL" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  <input className={inputClass} placeholder="PHONE" type="tel" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  <textarea className={`${inputClass} min-h-[80px] resize-none`} placeholder="NOTES (OPTIONAL)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                  <p className="text-xs text-white/40 text-center mt-2 mb-4">
                    By submitting this form, you agree to receive a one-time SMS confirmation from Sonz of Thunder Services. Msg &amp; data rates may apply. Reply STOP to opt out.
                  </p>
                  <button type="submit" disabled={status === 'sending'} className="w-full bg-electric hover:bg-electric-bright disabled:opacity-60 text-charcoal-deep font-display font-bold py-4 tracking-[0.25em] text-sm transition glow-electric">
                    {status === 'sending' ? 'SENDING…' : 'SUBMIT QUOTE REQUEST →'}
                  </button>
                  {status === 'error' && <p className="text-red-400 text-sm text-center">Something broke. Call (407) 461-6039.</p>}
                </form>
              )}
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
